// src/pages/locations/components/LocationFormPage.tsx
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";

import { canAccessLocationTab, type LocationEntity } from "../locations.access";

type Props = {
  /** Drives the permission check — same grant that shows the matching tab. */
  entity: LocationEntity;
  title: string;
  subtitle?: string;
  backTo: string;
  backLabel: string;

  isLoading?: boolean;
  loadingLabel?: string;
  error?: string | null;

  children: React.ReactNode;
};

/**
 * Shell for the Locations create/edit screens: page chrome, the permission
 * check, and the loading/error states an edit page needs while it fetches the
 * record named in its URL.
 */
const LocationFormPage: React.FC<Props> = ({
  entity,
  title,
  subtitle,
  backTo,
  backLabel,
  isLoading = false,
  loadingLabel = "Loading…",
  error = null,
  children,
}) => {
  // The sidebar already hides what a user cannot reach; this covers a typed or
  // shared URL.
  if (!canAccessLocationTab(entity)) return <Navigate to="/403" replace />;

  return (
    <AdminLayout>
      <div className="mb-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          <FiArrowLeft />
          {backLabel}
        </Link>

        <h1 className="mt-2 text-xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}

        <div className="mt-1">
          <Breadcrumbs />
        </div>
      </div>

      {/* The forms render their own sections and action bar, so the card is
          only a container here. */}
      <div className="max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-gray-500">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
            {loadingLabel}
          </div>
        )}

        {!isLoading && error && (
          <div className="space-y-4 p-5 sm:p-6">
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              {error}
            </div>
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiArrowLeft />
              {backLabel}
            </Link>
          </div>
        )}

        {!isLoading && !error && children}
      </div>
    </AdminLayout>
  );
};

export default LocationFormPage;
